import { Link, Navigate } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/Tag';
import { Button, IconButton } from '@/components/Button';
import { ErrorState, Skeleton } from '@/components/States';
import { money, num } from '@/lib/format';
import { parsePeriod, periodParam, periodQuery, useGet, useStlPerms, useUrlState } from './api';
import { EPS, LABELS, cashState } from './status';
import type { Scope, SummaryPayload, SummaryRow, SummaryTotals } from './types';
import { Kpis, PeriodBar, StlHeader, bigMoney, type Kpi } from './ui';
import s from './OverviewPage.module.css';

function pct(part: number, total: number) {
  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((part / total) * 100)));
}

/** 一侧（客户或供应商）：资金与发票两组数字 + 优先处理 */
function Side({ scope, rows, totals, keep }: { scope: Scope; rows: SummaryRow[]; totals: SummaryTotals; keep?: string }) {
  const L = LABELS[scope];
  const seg = scope === 'supplier' ? 'suppliers' : 'customers';
  const q = keep ? `?period=${keep}` : '';
  const amounts = totals.billAmount !== undefined;
  let open = 0, over = 0, invOpen = 0, invOver = 0;
  for (const r of rows) {
    const b = num(r.balanceAmount);
    const i = num(r.invoiceBalanceAmount);
    if (b > EPS) open += b; else if (b < -EPS) over -= b;
    if (i > EPS) invOpen += i; else if (i < -EPS) invOver -= i;
  }
  const bill = num(totals.billAmount);
  const inv = num(totals.invoiceRequiredAmount);
  const cashKpis: Kpi[] = [
    { label: L.bill, value: bigMoney(totals.billAmount) },
    { label: L.cash, value: bigMoney(totals.cashAmount), tone: 'blue' },
    { label: L.openAmount, value: bigMoney(open), tone: open > EPS ? 'bad' : 'mute' },
    { label: L.overAmount, value: bigMoney(over), tone: over > EPS ? 'over' : 'mute' },
  ];
  const invKpis: Kpi[] = [
    { label: L.invRequired, value: bigMoney(totals.invoiceRequiredAmount) },
    { label: L.inv, value: bigMoney(totals.invoiceAmount), tone: 'blue' },
    { label: L.invOpenAmount, value: bigMoney(invOpen), tone: invOpen > EPS ? 'warn' : 'mute' },
    { label: L.invOverAmount, value: bigMoney(invOver), tone: invOver > EPS ? 'blue' : 'mute' },
  ];
  // 优先处理：还欠着钱，或者票开多了（老系统 priorityRows 同一条件）；服务端已按差额绝对值倒序
  const priority = rows.filter((r) => num(r.balanceAmount) > EPS || num(r.invoiceBalanceAmount) < -EPS).slice(0, 5);
  return (
    <section className={s.side}>
      <header className={s.sh}>
        <h2>{scope === 'supplier' ? '供应商 · 应付与收票' : '客户 · 应收与开票'}</h2>
        <span className="tnum">{rows.length} 家</span>
        <Link to={`/settlements/${seg}${q}`} className={s.more}>{L.title}<Icon name="chev" size={14} /></Link>
      </header>
      <p className={s.desc}>{scope === 'supplier' ? '只统计采购单、付款和收票，不与客户收款相加。' : '只统计客户订单、收款和开票，不与供应商付款相加。'}</p>
      {amounts ? (
        <>
          <div className={s.group}>
            <div className={s.gh}><b>{L.cashGroup}</b><span className="tnum">{L.cashShort} {pct(num(totals.cashAmount), bill)}%</span></div>
            <div className={s.bar}><i style={{ width: `${pct(num(totals.cashAmount), bill)}%` }} /></div>
            <Kpis items={cashKpis} />
          </div>
          <div className={s.group}>
            <div className={s.gh}><b>{L.invGroup}</b><span className="tnum">{L.inv} {pct(num(totals.invoiceAmount), inv)}%</span></div>
            <div className={`${s.bar} ${s.barInv}`}><i style={{ width: `${pct(num(totals.invoiceAmount), inv)}%` }} /></div>
            <Kpis items={invKpis} />
          </div>
        </>
      ) : (
        <Kpis items={[
          { label: L.partner, value: `${rows.length} 家` },
          { label: L.docs, value: `${totals.orderCount} 张` },
          { label: L.cashRecord, value: `${totals.cashRecordCount} 笔` },
          { label: L.invRecord, value: `${totals.invoiceRecordCount} 笔` },
        ]} />
      )}
      <div className={s.prio}>
        <h3>优先处理{priority.length > 0 && <small>{L.open}或{L.invOver}的单位</small>}</h3>
        {priority.length ? (
          <ul>
            {priority.map((r) => {
              const cs = cashState(r.balanceAmount, scope);
              const b = num(r.balanceAmount);
              const i = num(r.invoiceBalanceAmount);
              return (
                <li key={r.partnerId}>
                  <Link to={`/settlements/${seg}/${r.partnerId}${q}`}>
                    <div>
                      <b>{r.partnerName}</b>
                      <small className="tnum">
                        {b > EPS && <span className={s.owe}>{L.open} {money(b)}</span>}
                        {i > EPS && <span className={s.warn}>{L.invOpen} {money(i)}</span>}
                        {i < -EPS && <span className={s.blue}>{L.invOver} {money(-i)}</span>}
                      </small>
                    </div>
                    {cs && <Tag tone={cs.tone} sm>{cs.label}</Tag>}
                    <Icon name="chev" className={s.chev} />
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className={s.none}>{rows.length ? `这段时间没有${L.open}的${L.partner}。` : `这段时间没有${L.partner}往来。`}</p>
        )}
      </div>
    </section>
  );
}

/** /settlements：客户应收与供应商应付分开统计，收付款和发票只在各自类别内抵扣（老系统总览口径） */
export function OverviewPage() {
  const ff = useFormFactor();
  const perms = useStlPerms();
  const [sp, set] = useUrlState();
  const period = parsePeriod(sp.get('period'));
  // 老系统总览的日期口径只有「创建时间」一项
  const summary = useGet<SummaryPayload>(perms.overview ? '/api/settlements/summary' : null, { ...periodQuery(period), dateBasis: 'createdAt' });

  if (!perms.overview) {
    const to = perms.customer ? '/settlements/customers' : perms.supplier ? '/settlements/suppliers' : perms.statement ? '/settlements/statements' : '/';
    return <Navigate to={to} replace />;
  }

  const keep = periodParam(period);
  const rows = summary.data?.rows ?? [];
  const body = (() => {
    if (summary.error && !summary.data) return <ErrorState title="结算总览加载失败" message={summary.error.message} detail={`GET ${summary.url}`} onRetry={summary.reload} />;
    if (!summary.data) return <div className={s.pad}><Skeleton /><Skeleton lines={5} /></div>;
    return (
      <div className={s.sides}>
        {perms.customer && <Side scope="customer" rows={rows.filter((r) => r.partnerType === 'CUSTOMER')} totals={summary.data.breakdown.customer} keep={keep} />}
        {perms.supplier && <Side scope="supplier" rows={rows.filter((r) => r.partnerType === 'SUPPLIER')} totals={summary.data.breakdown.supplier} keep={keep} />}
      </div>
    );
  })();

  const top = (
    <div className={s.top}>
      <PeriodBar period={period} onPeriod={(p) => set({ period: periodParam(p) })} basisText="创建时间" />
      {summary.loading && summary.data && <span className={s.updating}>更新中…</span>}
    </div>
  );

  if (ff === 'phone') {
    return (
      <div className={s.phone}>
        <StlHeader phone active="overview" keep={keep} actions={<IconButton name="ref" label="刷新" onClick={summary.reload} />} />
        <div className={s.scroll}>
          {top}
          {body}
          <p className={s.foot}>各行金额只代表该单位自身的应收、应付或发票口径。</p>
        </div>
      </div>
    );
  }
  return (
    <div className={s.page}>
      <StlHeader phone={false} active="overview" keep={keep} actions={<Button icon="ref" onClick={summary.reload}>刷新</Button>} />
      <div className={s.scroll}>
        {top}
        {body}
        <p className={s.foot}>客户应收与供应商应付分别统计；收付款和发票只在各自类别内抵扣，不做跨类别合并。</p>
      </div>
    </div>
  );
}
